import { useQueryClient } from "@tanstack/react-query";
import { type ServerError } from "src/graphql-next/fetcher";
import { useGetMeQuery as useGetMeQueryOnNewGateway } from "src/graphql-next/generated/queries";
import { type GetMeQuery as GetMeQueryOnNewGateway } from "src/graphql-next/generated/types";
import { useGetMeQuery } from "src/graphql/generated/queries";
import {
  FeatureFlagNamesEnum,
  type GetMeQuery,
} from "src/graphql/generated/types";
import { getCustomerDisplayNameFromGatewayNext } from "src/utils/customer/getCustomerDisplayName";

import { useHasFeatureFlagActivated } from "./useHasFeatureFlagActivated";

export type Me = {
  login: string;
  isTemporary: boolean;
  fullname: string | undefined;
};

export type UseGetMeResult = {
  data: { me: Me } | undefined;
  isLoading: boolean;
  error: ServerError | null;
};

export type UseGetMeQueryDataResult = {
  data: { me: Me } | undefined;
};

// The legacy-gateway fallback is load-bearing: first-connection SSO sessions
// (Plato temporary tokens from the association-confirmation email) are rejected
// with a 401 by the new gateway's `me`, which logs the user out before the
// confirmation page can render (PROD-30903). Keep it until the new gateway
// accepts temporary sessions.
export const useGetMe = (): UseGetMeResult => {
  const isGetMeOnNewGateway = useHasFeatureFlagActivated(
    FeatureFlagNamesEnum.CustomerPortalGetMeOnNewGateway,
  );

  const { data: dataOnNewGateway, ...queryResultOnNewGateway } =
    useGetMeQueryOnNewGateway(undefined, { enabled: isGetMeOnNewGateway });

  const { data, ...queryResult } = useGetMeQuery(undefined, {
    enabled: !isGetMeOnNewGateway,
  });

  return isGetMeOnNewGateway
    ? {
        data: dataOnNewGateway && { me: mapMe(dataOnNewGateway.me) },
        ...queryResultOnNewGateway,
      }
    : {
        data: data && { me: mapMe(data.me) },
        ...queryResult,
      };
};

export const useGetMeQueryData = (): UseGetMeQueryDataResult => {
  const queryClient = useQueryClient();

  const isGetMeOnNewGateway = useHasFeatureFlagActivated(
    FeatureFlagNamesEnum.CustomerPortalGetMeOnNewGateway,
  );

  const queryDataOnNewGateway =
    queryClient.getQueryData<GetMeQueryOnNewGateway>(
      useGetMeQueryOnNewGateway.getKey(),
    );

  const queryData = queryClient.getQueryData<GetMeQuery>(
    useGetMeQuery.getKey(),
  );

  return isGetMeOnNewGateway
    ? {
        data: queryDataOnNewGateway && { me: mapMe(queryDataOnNewGateway.me) },
      }
    : {
        data: queryData && { me: mapMe(queryData.me) },
      };
};

function mapMe(me: GetMeQuery["me"] | GetMeQueryOnNewGateway["me"]): Me {
  if (me.__typename === "Me") {
    return {
      login: me.login,
      isTemporary: me.isTemporary,
      fullname: me.fullname ?? undefined,
    };
  }

  return {
    login: me.login,
    isTemporary: me.isTemporaryLogin,
    fullname: getCustomerDisplayNameFromGatewayNext(me.customer) ?? undefined,
  };
}
